import type { StylePackCta } from '@/themes/stylePack.types';
import {
  CTA_DISABLED_STYLES,
  ctaDisabledVars,
  type CtaDisabledStyle,
  type CtaDisabledVars,
} from '@/themes/ctaDisabledStyle';

/**
 * Главная кнопка квиза своими полями воронки (слово владельца 21.10.2026) —
 * зеркало серверной схемы `backend/src/ai-funnels/block-schema/quiz-cta.ts`.
 * «Главная кнопка» — кнопка экрана: блок «Кнопка Далее» и запасная кнопка
 * плеера (QuizFallbackCta); кнопки внутри блоков сюда не относятся.
 *
 * «Перебивает пак» по каждому подполю отдельно: задано — берётся оно; не
 * задано — как решает пак (`cta.placement`, `cta.disabledStyle`), а без пака —
 * прежняя кнопка в потоке без приглушения. Ручной сейв кабинета идёт мимо
 * схемы сервера, поэтому разбор здесь — зажим отрисовки: негодное = нет поля.
 * Совпадение перечней и границы с сервером держит сторож `quiz-cta.ends.spec.ts`.
 */
export const QUIZ_CTA_PLACEMENTS = ['fixed-bottom', 'inline'] as const;
/**
 * Положение главной кнопки — один тип и для поля воронки, и для поля экрана
 * `EditorScreen.ctaPlacement` (задание stylepack/cta-screen-placement).
 */
export type QuizCtaPlacement = (typeof QUIZ_CTA_PLACEMENTS)[number];
/** Вид выключенной — перечень пака, не копия (ctaDisabledStyle.ts). */
export const QUIZ_CTA_DISABLED_STYLES = CTA_DISABLED_STYLES;
export const QUIZ_CTA_BOUNDS = {
  disabledOpacity: { min: 0.2, max: 1 },
  bottomOffset: { min: 0, max: 120 },
} as const;

export interface QuizCtaSettings {
  placement?: QuizCtaPlacement;
  disabledStyle?: CtaDisabledStyle;
  disabledOpacity?: number;
  /**
   * Отступ прибитой кнопки от низа окна, целые px (слово владельца
   * 21.10.2026, Petcube — 32). Без поля — отступ пака; кнопка в потоке —
   * ни на что не влияет (ctaDockStyle.ts, resolveCtaDock).
   */
  bottomOffset?: number;
}

const isOneOf = <T extends string>(list: readonly T[], v: unknown): v is T =>
  typeof v === 'string' && (list as readonly string[]).includes(v);

/** Разбор сырого `settings.quizCta`: годное поле остаётся, прочее выброшено. */
export function readQuizCta(raw: unknown): QuizCtaSettings {
  const out: QuizCtaSettings = {};
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out;
  const r = raw as Record<string, unknown>;
  if (isOneOf(QUIZ_CTA_PLACEMENTS, r.placement)) out.placement = r.placement;
  if (isOneOf(QUIZ_CTA_DISABLED_STYLES, r.disabledStyle))
    out.disabledStyle = r.disabledStyle;
  const o = r.disabledOpacity;
  const { min, max } = QUIZ_CTA_BOUNDS.disabledOpacity;
  if (typeof o === 'number' && o >= min && o <= max) out.disabledOpacity = o;
  const b = r.bottomOffset;
  const bb = QUIZ_CTA_BOUNDS.bottomOffset;
  if (Number.isInteger(b) && (b as number) >= bb.min && (b as number) <= bb.max)
    out.bottomOffset = b as number;
  return out;
}

/**
 * Зажим поля экрана `ctaPlacement` (ручной сейв идёт мимо схемы сервера):
 * значение перечня — оно, прочее — как будто поля нет.
 */
export function readScreenCtaPlacement(
  raw: unknown,
): QuizCtaPlacement | undefined {
  return isOneOf(QUIZ_CTA_PLACEMENTS, raw) ? raw : undefined;
}

export interface ResolvedQuizCta {
  placement: StylePackCta['placement'];
  /** Вид выключенной (поле, иначе пак); нет обоих — undefined. */
  disabledStyle: CtaDisabledStyle | undefined;
  /**
   * Переменные приглушения на самой кнопке — только когда поле задало вид или
   * прозрачность; иначе undefined и кнопка читает переменные пака с корня.
   */
  vars: CtaDisabledVars | undefined;
  /** Свой отступ прибитой кнопки от низа окна; нет поля — undefined (решает пак). */
  bottomOffset: number | undefined;
}

/**
 * Одна дверь «поле перебивает пак» для главной кнопки плеера. Положение —
 * по порядку: поле экрана `screenPlacement` (сырое `EditorScreen.ctaPlacement`,
 * задание stylepack/cta-screen-placement) → `settings.quizCta.placement` →
 * пак. Нет поля экрана или оно негодное — ровно прежний выбор. Прочие
 * подполя (вид выключенной, отступ) у экрана своих нет — от воронки и пака;
 * отступ действует только у прибитой кнопки (resolveCtaDock).
 */
export function resolveQuizCta(
  cta: StylePackCta | undefined,
  raw: unknown,
  screenPlacement?: unknown,
): ResolvedQuizCta {
  const c = readQuizCta(raw);
  const disabledStyle = c.disabledStyle ?? cta?.disabledStyle;
  return {
    placement:
      readScreenCtaPlacement(screenPlacement) ??
      c.placement ??
      (cta ? cta.placement : 'inline'),
    disabledStyle,
    vars:
      c.disabledStyle !== undefined || c.disabledOpacity !== undefined
        ? ctaDisabledVars(disabledStyle, c.disabledOpacity)
        : undefined,
    bottomOffset: c.bottomOffset,
  };
}

/**
 * Поля кнопки, которым нужен ленивый кусок рамки (`quizChromeKit`): прибитую
 * панель и затемнение `muted-brand-dark` рисует только он. Страница заказывает
 * кусок и по ним, а не только по паку (`usePlayerKitDoor`).
 */
export function quizCtaNeedsChromeKit(raw: unknown): boolean {
  const c = readQuizCta(raw);
  return (
    c.placement === 'fixed-bottom' || c.disabledStyle === 'muted-brand-dark'
  );
}

/**
 * Хоть один экран прибивает кнопку своим полем — прибитую панель рисует
 * только кусок рамки, страница заказывает его и по экранам
 * (`usePlayerKitDoor`, задание stylepack/cta-screen-placement).
 */
export function screensNeedCtaDock(screens: unknown): boolean {
  if (!Array.isArray(screens)) return false;
  return screens.some(
    (s) =>
      !!s &&
      typeof s === 'object' &&
      readScreenCtaPlacement((s as { ctaPlacement?: unknown }).ctaPlacement) ===
        'fixed-bottom',
  );
}
